import { format, isValid, parseISO } from 'date-fns'

import type { DueDateInput } from './due-date'

/**
 * Helpers de formato que **no dependen del idioma**.
 *
 * Todo lo que sí depende —fechas legibles, números, tiempos relativos y
 * plurales— vive en `@/shared/i18n`, porque necesita saber quién está mirando.
 * `18/09/2026` y `09/18/2026` son la misma fecha en dos convenciones, y elegir
 * cuál va es una decisión de idioma, no de utilidad.
 *
 * En particular **ya no existe `pluralize(count, 'tarea')`**: pegar una `s` al
 * final funciona en español y se rompe en cuanto alguien traduce ("3 overdue
 * tasks" no pone el número donde lo ponía "3 tareas vencidas", y en portugués
 * el `0` va en singular). Esas frases son entradas del catálogo con formas
 * plurales, y las elige CLDR.
 */

export function toDate(value: DueDateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return isValid(value) ? value : null
  if (typeof value === 'number') {
    const fromNumber = new Date(value)
    return isValid(fromNumber) ? fromNumber : null
  }
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

/** `2026-09-17`, para inputs `type="date"` y claves de filtro en la URL. */
export function formatDateInput(value: DueDateInput): string {
  const date = toDate(value)
  if (!date) return ''
  return format(date, 'yyyy-MM-dd')
}

/** Monograma de 2 letras: `Leaf Latam` -> `LL`, `Siete` -> `SI`. */
export function initials(name: string, length = 2): string {
  const words = name
    .trim()
    .split(/[\s\-_]+/)
    .filter(Boolean)

  if (words.length === 0) return '??'
  if (words.length === 1) {
    return (words[0] ?? '').slice(0, length).toUpperCase().padEnd(length, '·')
  }
  return words
    .slice(0, length)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
}

export function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, Math.max(0, max - 1))}…`
}

/**
 * Un número que puede no estar.
 *
 * `null` no es cero: el backend no siempre devuelve los contadores (el listado
 * de empresas no trae ninguno, y el overview no separa tareas abiertas de
 * vencidas). Mostrar `0` donde no sabemos se lee como "no tiene ninguno", que
 * es una afirmación que no podemos hacer. Un guión dice la verdad: no lo sé.
 */
export function countOrDash(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : String(value)
}

/**
 * Slug a partir de un nombre: para URLs, canales y todo lo que no tolera
 * acentos.
 *
 * Quita los diacríticos en vez de mapearlos a mano porque el vocabulario real
 * los tiene ("Ñandú", "Kaizen Legal") y un slug con `ñ` entra en una URL como
 * `%C3%B1`, que es ilegible justo donde tiene que leerse.
 */
export function slugify(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50)
}
